<template>
  <div class="relative w-full">
    <RevRadio
      :id
      :aria-describedby="`${id}-description`"
      :aria-labelledby="`${id}-label`"
      :class="[
        $style.radio,
        {
          [$style.blueHighlighted]: isHighlighted,
        },
      ]"
      :model-value="isSelected"
      :tagProps
      :value="id"
      variant="full"
      @click="handleClick"
    >
      <template #label>
        <div :id="`${id}-label`" class="space-y-8">
          <span v-if="tagProps" class="sr-only">{{ tagProps.label }}</span>

          <span class="body-1-bold">{{ offer.title }}</span>

          <div class="flex gap-8">
            <template v-if="offer.price && offer.price.amount !== '0.00'">
              <InsurancePrice
                :payment-frequency="offer.paymentFrequency"
                :price="offer.price"
              />
            </template>

            <div
              v-if="
                isAnnualRecurringIterationEnabled &&
                offer.paymentFrequency === 'ANNUALLY'
              "
            >
              <RevTag
                :label="i18n(translations.saveOnAnnuallyOffer)"
                variant="primary"
              />
            </div>
          </div>
        </div>
      </template>
      <template #description>
        <RevDivider
          class="relative -inset-x-[38px] mb-16 mt-8 w-[calc(100%+38px)]"
        />

        <ul
          v-if="
            offer.paymentFrequency === 'ANNUALLY' &&
            isAnnualRecurringIterationEnabled
          "
          :id="`${id}-description`"
          class="relative -inset-x-8 mt-8 space-y-8"
        >
          <li
            v-for="benefit in annualRecurringIterationBenefits"
            :key="benefit"
            class="flex items-center"
          >
            <IconCheckSmall class="shrink-0 self-start" /> {{ benefit }}
          </li>
        </ul>

        <ul
          v-else
          :id="`${id}-description`"
          class="relative -inset-x-8 mt-8 space-y-8"
        >
          <li
            v-for="benefit in offer.benefits.short"
            :key="benefit"
            class="flex items-center"
          >
            <IconCheckSmall class="shrink-0 self-start" /> {{ benefit }}
          </li>
        </ul>
      </template>
    </RevRadio>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { InsuranceOffer } from '@backmarket/http-api/src/api-models/InsuranceOffer'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevDivider } from '@ds/components/Divider'
import { RevRadio } from '@ds/components/Radio'
import { RevTag } from '@ds/components/Tag'
import { VariantsEnum } from '@ds/components/Tag/Tag.constant'
import { IconCheckSmall } from '@ds/icons/IconCheckSmall'

import InsurancePrice from '../InsurancePrice/InsurancePrice.vue'

import translations from './InsuranceRadio.translations'

const { trackClick } = useTracking()
const experiments = useExperiments()
const i18n = useI18n()

const props = defineProps<{
  id: string
  offer: InsuranceOffer
  isSelected: boolean
  isHighlighted: boolean
  trackingZone: string
  tagProps: { label: string; variant: VariantsEnum } | undefined
}>()

const emit = defineEmits(['select', 'remove'])

const isAnnualRecurringIterationEnabled = computed(
  () =>
    experiments['experiment.annualRecurringIteration'] ===
    'withAnnualRecurringIteration',
)

const annualRecurringIterationBenefits = computed(() => [
  i18n(translations.annualRecurringIterationBenefit1),
  i18n(translations.annualRecurringIterationBenefit2),
  i18n(translations.annualRecurringIterationBenefit3),
  i18n(translations.annualRecurringIterationBenefit4),
  i18n(translations.annualRecurringIterationBenefit5),
])

function handleClick() {
  trackClick({
    name: props.offer.title,
    value: props.offer.paymentFrequency,
    zone: props.trackingZone,
  })

  emit(props.isSelected ? 'remove' : 'select')
}
</script>

<style lang="css" module>
.radio {
  @apply h-full;
}

.radio > label {
  @apply h-full;
}

.radio > label > [aria-hidden='true'] {
  @apply overflow-hidden;
}

.radio > label > [aria-hidden='true']::before {
  content: '';
  @apply pointer-events-none absolute inset-y-0 left-0 border-l-6 border-l-static-default-mid;
}

.radio.blueHighlighted > label {
  @apply hover:bg-static-info-low;
}

.radio.blueHighlighted > label > [aria-hidden='true']::before {
  @apply border-l-static-accent-hi;
}
</style>
